import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import api from "@/lib/api";

/**
 * Nov 2026 (SCWT-34E6D6) — full list of paid Mail-Out Kit bookings that
 * can't get a shipping label yet. Reads
 * GET /api/admin/posted-kit/needing-address-repair, which uses the SAME
 * `posted_kit_label_eligibility` function as the Create-Label gate and
 * the next-action hint — this list can never drift from what actually
 * blocks a label.
 *
 * Read-only: each row links to the booking's own detail page, where the
 * existing "Posted sample kit fulfilment" panel shows a SUGGESTED
 * address (parsed from the original free-text booking notes) that staff
 * must review, edit if needed, and explicitly Save — nothing here
 * auto-approves an address, flags postage as paid, or creates a label.
 */
export default function PostedKitAddressRepairList() {
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get("/admin/posted-kit/needing-address-repair")
      .then(({ data }) => setState({ loading: false, data, error: null }))
      .catch((e) => setState({ loading: false, data: null, error: e?.response?.data?.detail || "Failed to load" }));
  };

  useEffect(load, []);

  const rows = state.data?.bookings || [];
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return iso; }
  };
  const fmtAmount = (v) => (v == null ? "—" : `$${Number(v).toFixed(2)}`);
  const reasonLabel = (code) => ({
    missing_address: "No delivery address on file",
    status_mismatch: "Address on file, shipping status not updated",
  }[code] || code || "—");

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6" data-testid="posted-kit-address-repair-list">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-700 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> Dashboard
        </Link>

        <div className="flex items-center justify-between mb-1">
          <h1 className="text-xl font-bold text-slate-900">Mail-Out Kits Needing a Delivery Address</h1>
          <button
            type="button"
            onClick={load}
            data-testid="posted-kit-address-repair-refresh"
            className="inline-flex items-center gap-1.5 text-[12px] text-slate-500 hover:text-slate-700"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
        <p className="text-[13px] text-slate-500 mb-5">
          These are paid Mail-Out Kit bookings that can't get a shipping label yet because checkout never
          captured a confirmed delivery address. Open each booking to review the suggested address (taken
          from the original booking) and save it — nothing here is approved automatically.
        </p>

        {state.loading ? (
          <div className="flex items-center gap-2 text-slate-500 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : state.error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-700 text-[13px] p-3">{String(state.error)}</div>
        ) : (
          <>
            <div
              data-testid="posted-kit-address-repair-count-banner"
              className={`mb-4 rounded-lg border px-3 py-2 text-[13px] font-semibold ${rows.length ? "border-amber-200 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}
            >
              {rows.length} booking{rows.length === 1 ? "" : "s"} currently need{rows.length === 1 ? "s" : ""} a confirmed delivery address.
            </div>

            {rows.length > 0 && (
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <table className="w-full text-[13px]">
                  <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Reference</th>
                      <th className="text-left px-3 py-2">Customer</th>
                      <th className="text-left px-3 py-2">Booked</th>
                      <th className="text-left px-3 py-2">Amount</th>
                      <th className="text-left px-3 py-2">Reason</th>
                      <th className="text-left px-3 py-2">Suggested address on file</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rows.map((r) => (
                      <tr key={r.reference} data-testid={`posted-kit-repair-row-${r.reference}`}>
                        <td className="px-3 py-2 font-mono text-[12px]">{r.reference}</td>
                        <td className="px-3 py-2">{r.customer_name || "—"}</td>
                        <td className="px-3 py-2">{fmtDate(r.booked_at)}</td>
                        <td className="px-3 py-2">{fmtAmount(r.amount)}</td>
                        <td className="px-3 py-2 text-slate-600">{reasonLabel(r.reason_code)}</td>
                        <td className="px-3 py-2 text-slate-500 max-w-[220px] truncate" title={r.legacy_address || ""}>
                          {r.legacy_address || "—"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Link
                            to={`/admin/bookings/${r.reference}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`posted-kit-repair-open-${r.reference}`}
                            className="inline-flex items-center gap-1 text-cyan-700 hover:text-cyan-900 font-semibold"
                          >
                            Review <ExternalLink className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
